import React from 'react';
import { Button, Section, SectionHeader } from '../../design-system';
import MarketingLayout from '../layouts/MarketingLayout';
import FadeIn from '../components/FadeIn';
import TypingDemo from '../components/TypingDemo';
import SchedulerDemo from '../components/SchedulerDemo';
import ConversationalDemo from '../components/ConversationalDemo';
import { AI_FEATURES } from '../data/content';
import SeoHead from '../seo/SeoHead';
import { PRODUCT_SEO } from '../seo/pages';

const HOW_IT_WORKS = [
  { step: '01', title: 'Type your message', description: 'Tell INVERT what you want to display — in plain English.' },
  { step: '02', title: 'AI composes', description: 'Layout, typography, colors, animations, and imagery — generated instantly.' },
  { step: '03', title: 'Review & refine', description: 'Conversationally edit: "Make it bigger." "Add our logo." "Use brand colors."' },
  { step: '04', title: 'Publish everywhere', description: 'One click sends it to every screen. Schedule with natural language.' },
];

export default function Product() {
  return (
    <MarketingLayout>
      <SeoHead title={PRODUCT_SEO.title} description={PRODUCT_SEO.description} path={PRODUCT_SEO.path} />
      <section className="bg-invert-navy text-white pt-32 lg:pt-40 pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <FadeIn>
            <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange mb-6">
              Product
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 max-w-4xl mx-auto">
              Transform every screen into an intelligent business display
            </h1>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto mb-10">
              INVERT is the AI operating system for customer-facing screens. Type what you want. INVERT designs, schedules, and publishes — automatically.
            </p>
            <Button to="/screenHostSignup" size="lg">Get Started</Button>
          </FadeIn>
        </div>
      </section>

      <Section>
        <SectionHeader
          label="How It Works"
          title="Prompt → Layout engine → Screen"
          subtitle="No design tools. No file uploads. No complicated software."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {HOW_IT_WORKS.map((item, i) => (
            <FadeIn key={item.step} delay={i * 0.1}>
              <div className="relative">
                <span className="text-5xl font-bold text-invert-orange/20">{item.step}</span>
                <h3 className="text-lg font-semibold text-invert-navy mt-2 mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section className="bg-slate-50">
        <SectionHeader
          label="AI Composer"
          title="ChatGPT for digital signage"
          subtitle="The primary way businesses create content. Type. Generate. Publish."
        />
        <TypingDemo />
      </Section>

      {AI_FEATURES.map((feat, i) => (
        <Section key={feat.title} className={i % 2 === 1 ? 'bg-slate-50' : ''}>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <FadeIn className={i % 2 === 1 ? 'lg:order-2' : ''}>
              <p className="text-xs font-semibold uppercase tracking-widest text-invert-orange mb-4">
                {feat.title}
              </p>
              <h2 className="text-3xl font-bold text-invert-navy mb-4">{feat.title}</h2>
              <p className="text-lg text-slate-600 leading-relaxed">{feat.description}</p>
            </FadeIn>
            <FadeIn delay={0.15} className={i % 2 === 1 ? 'lg:order-1' : ''}>
              {feat.title === 'AI Scheduler' ? (
                <SchedulerDemo />
              ) : feat.title === 'AI Suggestions' ? (
                <ConversationalDemo />
              ) : (
                <div className="rounded-2xl bg-invert-navy p-8 text-center">
                  <span className="text-6xl">{feat.icon}</span>
                  <p className="text-white mt-4 text-lg font-medium">{feat.title}</p>
                </div>
              )}
            </FadeIn>
          </div>
        </Section>
      ))}

      <Section dark className="text-center">
        <FadeIn className="max-w-2xl mx-auto">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
            Update every screen in seconds
          </h2>
          <p className="text-slate-300 text-lg mb-8">
            Join businesses using INVERT to communicate professionally — without ever opening a design tool.
          </p>
          <Button to="/screenHostSignup" size="lg">Get Started</Button>
        </FadeIn>
      </Section>
    </MarketingLayout>
  );
}
